import { useEffect, useRef, useState } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/transitional/components/View';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { useResolvePublicShareLinkMutation } from '../../../__generated__/chirp/com/hubspot/breeze/assistant/interactive/experience/rpc/BreezeAssistantPublicArtifactsRpc.hooks';

// Height used before the artifact reports its own; it grows to fit on the first
// message, so this only shows for the initial paint.
import { jsx as _jsx } from "react/jsx-runtime";
const INITIAL_HEIGHT = 200;
function ArtifactMessage({
  children
}) {
  return /*#__PURE__*/_jsx(View, {
    padding: "500",
    backgroundColor: "fill-surface-raised",
    color: "text-core-subtle",
    children: children
  });
}

/**
 * Injects a small reporter into the (sandboxed, script-enabled) artifact document
 * that measures its own height and posts it to the parent, so the iframe can be
 * sized to the content instead of a fixed height. Uses `postMessage`, which works
 * across the sandbox boundary WITHOUT `allow-same-origin` (the parent can't read
 * the iframe's DOM directly, so it can't measure it itself).
 */
function withHeightReporter(html) {
  const reporter = `<script>(function(){function post(){var h=Math.max(document.documentElement.scrollHeight,document.body?document.body.scrollHeight:0);parent.postMessage({__breezeArtifactHeight:h},'*');}window.addEventListener('load',post);window.addEventListener('resize',post);if(window.ResizeObserver){try{new ResizeObserver(post).observe(document.documentElement);}catch(e){}}setTimeout(post,0);setTimeout(post,300);})();</script>`;
  return html.includes('</body>') ? html.replace('</body>', () => `${reporter}</body>`) : `${html}${reporter}`;
}

/**
 * Buyer-facing render of a BREEZE_ARTIFACT block: resolves the public share
 * `accessToken` via the EXTERNAL_NO_AUTH `resolvePublicShareLink` RPC (no HubSpot
 * login needed) and drops the returned HTML into a sandboxed `<iframe srcdoc>`,
 * so the artifact renders isolated from the portal's own styles/scripts. There
 * is no public embed URL — the RPC returns the raw HTML content — so srcdoc is
 * the embed. The iframe auto-sizes to the artifact's content height. In edit mode
 * the iframe is click-through so the block stays selectable/draggable.
 */
export function BreezeArtifactView({
  accessToken,
  editing
}) {
  const [resolve, {
    data,
    loading,
    error
  }] = useResolvePublicShareLinkMutation();
  const iframeRef = useRef(null);
  // Tag the measured height with the token it belongs to, so switching artifacts
  // derives back to the initial height without a reset effect.
  const [measured, setMeasured] = useState();
  const height = (measured === null || measured === void 0 ? void 0 : measured.token) === accessToken ? measured.height : undefined;
  useEffect(() => {
    if (accessToken) {
      // A failure shows through the hook's `error`.
      resolve({
        variables: {
          accessToken
        }
      }).catch(() => {});
    }
  }, [accessToken, resolve]);

  // Grow the iframe to whatever height its content reports (see withHeightReporter).
  useEffect(() => {
    function onMessage(event) {
      var _event$data;
      const iframe = iframeRef.current;
      if (!iframe || event.source !== iframe.contentWindow) {
        return;
      }
      const reported = (_event$data = event.data) === null || _event$data === void 0 ? void 0 : _event$data.__breezeArtifactHeight;
      if (typeof reported === 'number' && reported > 0) {
        setMeasured({
          token: accessToken,
          height: Math.ceil(reported)
        });
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [accessToken]);
  if (!accessToken) {
    return /*#__PURE__*/_jsx(ArtifactMessage, {
      children: "Select a Breeze artifact and add its public share token."
    });
  }
  if (error) {
    return /*#__PURE__*/_jsx(ArtifactMessage, {
      children: "Couldn\u2019t load this Breeze artifact."
    });
  }
  if (loading || !data) {
    return /*#__PURE__*/_jsx(Flex, {
      justifyContent: "center",
      paddingBlock: "700",
      children: /*#__PURE__*/_jsx(LoadingSpinner, {})
    });
  }
  if (data.outcome !== 'SUCCESS') {
    const message = data.outcome === 'PASSWORD_REQUIRED' ? 'This artifact is password protected.' : data.outcome === 'ACCESS_REQUIRED' ? 'This artifact requires access to view.' : 'This artifact is unavailable.';
    return /*#__PURE__*/_jsx(ArtifactMessage, {
      children: message
    });
  }
  if (!data.content) {
    return /*#__PURE__*/_jsx(ArtifactMessage, {
      children: "This artifact has no viewable content."
    });
  }
  return /*#__PURE__*/_jsx("iframe", {
    ref: iframeRef,
    title: data.title || 'Breeze artifact',
    srcDoc: withHeightReporter(data.content),
    sandbox: "allow-scripts allow-popups allow-forms",
    style: {
      width: '100%',
      height: height !== null && height !== void 0 ? height : INITIAL_HEIGHT,
      border: 0,
      borderRadius: 8,
      display: 'block',
      // Keep the block selectable/draggable in the editor canvas.
      pointerEvents: editing ? 'none' : 'auto'
    }
  });
}